import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getErrorMessages, isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import XCircle from "@insite/mobius/Icons/XCircle";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { selectItem } from "@WhiteCap/Common/Utillities/CustomTracking";
import MultipleSaleQtyMessage from "@WhiteCap/Components/MultipleSaleQtyMessage";
import PromotionPopup from "@WhiteCap/Components/Promotions/PromotionPopup";
import updateCartLineExpanded from "@WhiteCap/Handlers/Cart/UpdateCartLineExpanded";
import promotionHandler from "@WhiteCap/Handlers/Promotions/PromotionHandler";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import { cartlineDvb, dvbCartline } from "@WhiteCap/Utils/DvbCartline";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";

import React, { FC, useContext, useEffect, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel;
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    // updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    updateCartLineExpanded: HandleThunkActionCreator<typeof updateCartLineExpanded>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    eolProducts: state.WhiteCap.promotionProducts.eolProducts,
    customBuyerRoleName: state.context.settings.properties.customBuyerRoleName,
    userRoles: state.context.session.userRoles,
    isAuthenticated: state.context.session.isAuthenticated,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    removeCartLine,
    updateCartLineExpanded,
    promotionHandler,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    multipleSaleQtyGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItems?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    priceLabelText?: TypographyPresentationProps;
    priceLabelUnitNetPriceText?: TypographyPresentationProps;
    hidden?: GridItemProps;
    quantityGridItems?: GridItemProps;
    productPriceExtendedUnitNetPriceText: TypographyProps;
    productPriceAndUnitNetPrice?: GridItemProps;
    productPriceValue?: ProductPriceStyles;
    limitedTimeOfferContainer?: GridItemProps;
    limitedTimeOfferLabelText?: TypographyPresentationProps;
}

// EC-941 [Code Optimization] Cart Page
export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        css: css`
            padding-top: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: {
        width: [10, 10, 10, 11, 11],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        column-gap: 12px;
                        padding: 15px 10px;
                    `,
                    css``,
                    css``,
                    css``,
                    css``,
                ])}
        `,
    },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 7, 7] },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    multipleSaleQtyGridItem:{
        width: 12,
    },
    quantityAndExtendedUnitNetPriceGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    quantityGridItem: {
        width: 12,
        css: css`
            grid-row-start: 1;
            grid-column-start: 1;
        `,
    },
    extendedUnitNetPriceGridItem: {
        width: 12,
        css: css`
            display: contents;
            padding: 0px !important;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "normal",
        css: css`
            font-size: 16px;
            font-family: inherit;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndUnitNetPrice: {
        width: [12, 12, 12, 12, 12],
        css: css`
            grid-row-start: 1;
            flex-direction: column;
        `,
    },
    productPriceAndAvailabilityGridItems: {
        width: [6, 6, 6, 5, 5],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: {
        width: 12,
        css: css`
            padding: 6px;
            button {
                padding: 10px 15px;
                min-height: unset;
            }
        `,
    },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 15px 10px;
                        justify-content: end;
                    `,
                    css``,
                    css``,
                    css``,
                    css``,
                ])}
        `,
        width: [2, 2, 2, 1, 1],
    },
    removeCartLineIcon: { src: XCircle, color: `${WhitecapColors.createAccountHeaderBtnColor}` },
    binInformationsGridItem: {
        width: 12,
    },
    priceLabelText: {
        variant: "legend",
        css: css`
            font-size: 12px;
            font-family: inherit;
            font-weight: normal;
        `,
    },
    priceLabelUnitNetPriceText: {
        variant: "legend",
        css: css`
            font-size: 12px;
            font-family: inherit;
            font-weight: normal;
            padding-left: 0px;
        `,
    },
    productPartNumbers: {
        erpNumberValueText: {
            css: css`
                font-size: 14px;
            `,
        },
        erpNumberLabelText: {
            css: css`
                font-size: 14px;
            `,
        },
        manufacturerItemLabelText: {
            css: css`
                font-size: 14px;
            `
        },
        manufacturerItemValueText: {
            css: css`
                font-size: 14px;
            `
        },
    },
    productPrice: {
        price: {
            priceText: {
                css: css`
                    font-size: 14px;
                    font-family: inherit;
                    font-weight: normal;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    css: css`
                        font-size: 14px;
                        font-family: inherit;
                        font-weight: normal;
                    `,
                },
            },
        },
    },
    productPriceValue: {
        price: {
            priceText: {
                css: css`
                    font-size: 12px;
                    font-family: inherit;
                    font-weight: normal;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    css: css`
                        font-size: 12px;
                        font-family: inherit;
                        font-weight: normal;
                    `,
                },
            },
        },
    },
    hidden: {
        width: 12,
    },
    quantityGridItems: {
        // width: 12,
        width: [9, 9, 9, 12, 12],
        css: css`
            padding-left: 0px;
            padding-top: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 63%;
                        `,
                        css`
                            max-width: 63%;
                        `,
                        css`
                            max-width: 63%;
                        `,
                        css``,
                        css``,
                    ],
                    "max",
                )}
        `,
    },
    quantityAndExtendedUnitNetPriceContainer: {
        offsetCss: css`
            display: grid !important;
        `,
    },
    quantity: {
        editableQuantityTextField: {
            labelProps: {
                css: css`
                    font-size: 10px;
                    font-family: Gotham-Medium;
                    margin-top: 0px;
                `,
            },
            cssOverrides: {
                formInputWrapper: css`
                    margin-top: 0px !important;
                `,
            },
        },
    },
    productPriceExtendedUnitNetPriceText: {
        weight: "bold",
        css: css`
            font-size: 14px;
            font-family: inherit;
        `,
    },
    productImage: {
        img: {
            css: css`
                height: auto;
                max-height: 187px;
                margin: 0px;
            `,
        },
        linkWrappingImage: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                padding-top: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                height: 150px;
                                width: 100px;
                                margin-right: 5px;
                            `,
                            null,
                            css`
                                height: 187px;
                                width: 121px;
                                margin-right: 10px;
                            `,
                        ],
                        "min",
                    )}
            `,
        },
    },
    productBrand: {
        nameLink: {
            typographyProps: {
                css: css`
                    font-size: 14px !important;
                    font-family: inherit !important;
                    font-weight: bold;
                `,
            },
        },
    },
    productDescription: {
        productDetailLink: {
            typographyProps: {
                css: css`
                    font-size: 14px;
                    font-family: inherit;
                    font-weight: medium;
                `,
            },
        },
    },
    limitedTimeOfferContainer: {
        width: [12, 12, 12, 12, 12],
        css: css`
            flex-basis: auto !important;
            margin: 0 0px 15px !important;
            padding: 0;
            width: 100%;
        `,
    },
    limitedTimeOfferLabelText: {
        css: css`
            font-size: 12px;
            font-family: inherit;
            font-weight: 400;
            /* padding: 2px 20px; */
            background: ${WhitecapColors.yellow2};
            border-radius: 5px;
            color: ${WhitecapColors.iconColor};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 5px;
                    `,
                    css`
                        padding: 5px;
                    `,
                    css`
                        padding: 2px 20px;
                    `,
                    css`
                        padding: 2px 20px;
                    `,
                    css`
                        padding: 2px 20px;
                    `,
                ])}
        `,
    },
    promotionNameText: {
        css: css`
            font-weight: 600;
        `,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showAddToList = true,
    showProductNumber = true,
    removeCartLine,
    updateCartLineExpanded,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    promotionHandler,
    eolProducts,
    customBuyerRoleName,
    userRoles,
    isAuthenticated,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [currentPromotionType, setCurrentPromotionType] = React.useState<string | null>(null);


    const openPopup = () => {
        if (cartLine?.properties?.promotionId) {
            promotionHandler({ promotionId: cartLine.properties.promotionId });
        }
        setCurrentPromotionType('OTHER'); 
        setIsPopupOpen(true);
       
    };

    const openEolPopup = () => {
        const eolProduct = eolProducts && cartLine.productId && eolProducts[cartLine.productId];
        if (eolProduct) {
            const actualPrice = eolProduct.pricing.actualPrice;
            promotionHandler({
                promotionId: cartLine.properties.promotionId,
                promotionDetail: {
                    type: cartLine.properties.promotionId,
                    amount: actualPrice,
                },
            });
            setCurrentPromotionType('EOL'); 
            setIsPopupOpen(true);
        }
    };

    const closePopup = () => setIsPopupOpen(false);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({
                    cartLineId: cartLine.id,
                });
            } else {
                updateCartLineExpanded({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                    previousQty: cartLine.qtyOrdered,
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLineExpanded({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLineExpanded({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }
        removeCartLine({
            cartLineId: cartLine.id,
        });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLineExpanded({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

 const errorMessages = getErrorMessages(cart, cartLine, showInventoryAvailability);
   
    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);
    
    const isDbvProduct = cartlineDvb(cartLine);
    
    if (isDbvProduct) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequestedOnDBVProduct",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

   
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    const selectProduct = () => {
        if (cartLine) {
            selectItem({
                event: "select_item",
                ecommerce: {
                    items: [
                        {
                            item_name: cartLine.shortDescription,
                            item_id: cartLine.erpNumber,
                            price: cartLine.pricing?.unitNetPrice ,
                            item_brand: cartLine.brand?.name ? cartLine.brand?.name : "",
                        },
                    ],
                },
            });
        }
    };

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.cartLineInfoGridItem}>
                {showImage && (
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} onClick={selectProduct} />
                )}
                <GridContainer {...styles.cartLineInfoContainer}>
                    <GridItem {...styles.productInfoGridItem}>
                        <GridContainer {...styles.productInfoContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {showBrand && cartLine.brand && (
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                {showProductTitle && (
                                    <ProductDescription
                                        product={cartLine}
                                        extendedStyles={styles.productDescription}
                                        onClick={selectProduct}
                                    />
                                )}
                            </GridItem>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <GridItem {...styles.configurationGridItem}>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            <GridItem {...styles.productPartNumbersGridItem}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbers}
                                    showProductNumber={showProductNumber}
                                />
                            </GridItem>
                            <GridItem {...styles.multipleSaleQtyGridItem}>
                                <MultipleSaleQtyMessage product={cartLine} pageType={"Cart"} isAuthenticated={isAuthenticated}></MultipleSaleQtyMessage>
                            </GridItem>
                            {showVmiBinDetails && currentMode === "Vmi" && (
                                <GridItem {...styles.binInformationsGridItem}>
                                    <ProductBinNumbers
                                        locationId={cart.vmiLocationId}
                                        binId={cartLine.vmiBinId}
                                        isCondensed={false}
                                        extendedStyles={styles.productBinNumbers}
                                    />
                                </GridItem>
                            )}
                            <Hidden above="md">
                                <GridItem {...styles.quantityAndExtendedUnitNetPriceGridItem}>
                                    {!(userRoles && userRoles.includes(customBuyerRoleName)) && 
                                        !cartLine.isPromotionItem &&
                                        cartLine.properties?.promoFlag &&
                                        cartLine.properties.promoFlag.toLowerCase() === "true" && (
                                            <GridItem {...styles.limitedTimeOfferContainer}>
                                                <Typography as="span" {...styles.limitedTimeOfferLabelText}>
                                                    {siteMessage("Limited_Time_Offer_Message")}
                                                </Typography>
                                            </GridItem>
                                        )}
                                    <GridContainer {...styles.quantityAndExtendedUnitNetPriceContainer}>
                                        <GridItem {...styles.quantityGridItem}>
                                            <CartLineQuantity
                                                cart={cart}
                                                editable={true}
                                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                                extendedStyles={styles.quantity}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.productPriceAndUnitNetPrice}>
                                            {!cartLine.quoteRequired &&
                                                !cart.cartNotPriced &&
                                                cartLine.pricing &&
                                                showPricing && (
                                                    <>
                                                        <Typography {...styles.priceLabelUnitNetPriceText}>
                                                            {translate("Price")}
                                                        </Typography>
                                                        <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                                            <Typography
                                                                {...styles.productPriceExtendedUnitNetPriceText}
                                                                data-test-selector="cartline_extendedUnitNetPrice"
                                                            >
                                                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                                    ? cartLine.pricing
                                                                        .extendedUnitRegularPriceWithVatDisplay
                                                                    : cartLine.pricing.extendedUnitNetPriceDisplay}
                                                            </Typography>
                                                            {enableVat && (
                                                                <>
                                                                    <Typography as="p" {...styles.vatLabelText}>
                                                                        {vatPriceDisplay === "DisplayWithVat" ||
                                                                        vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                                            ? `${translate("Inc. VAT")} (${
                                                                                cartLine.pricing.vatRate
                                                                            }%)`
                                                                            : translate("Ex. VAT")}
                                                                    </Typography>
                                                                    {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                                        <>
                                                                            <Typography {...styles.subtotalWithoutVatText}>
                                                                                {
                                                                                    cartLine.pricing
                                                                                        .extendedUnitNetPriceDisplay
                                                                                }
                                                                            </Typography>
                                                                            <Typography as="p" {...styles.vatLabelText}>
                                                                                {translate("Ex. VAT")}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                        </GridItem>
                                                    </>
                                                )}
                                            {!cart.cartNotPriced && (
                                                <ProductPrice
                                                    product={cartLine}
                                                    currencySymbol={cart.currencySymbol}
                                                    showSavings={!cart.properties["isPunchout"]}
                                                    showSavingsAmount={productSettings.showSavingsAmount}
                                                    showSavingsPercent={productSettings.showSavingsPercent}
                                                    showInvalidPriceMessage={true}
                                                    extendedStyles={styles.productPriceValue}
                                                    showLabel={false}
                                                    showUnitOfMeasure={showPerUnitPrice}
                                                />
                                            )}
                                            <Hidden below="lg">
                                                <GridItem {...styles.quantityGridItems}>
                                                    <CartLineQuantity
                                                        cart={cart}
                                                        editable={true}
                                                        onQtyOrderedChange={qtyOrderedChangeHandler}
                                                        extendedStyles={styles.quantity}
                                                    />
                                                </GridItem>
                                            </Hidden>
                                            {cartLine.isSubscription && cartLine.productSubscription && (
                                                <ProductDeliveryScheduleButton
                                                    subscription={
                                                        cartLine.properties.productSubscription
                                                            ? JSON.parse(cartLine.properties.productSubscription)
                                                            : cartLine.productSubscription
                                                    }
                                                    disabled={
                                                        !cart.canModifyOrder ||
                                                        cartLine.isPromotionItem ||
                                                        !cartLine.qtyOrdered ||
                                                        cartLine.qtyOrdered <= 0
                                                    }
                                                    onSave={saveScheduleHandler}
                                                    extendedStyles={styles.deliveryScheduleButton}
                                                />
                                            )}
                                        </GridItem>
                                        <GridItem width={12}>
                                        {/* //Not Showing Promotion Name for EOL value parent item */}
                                        {promotions?.map(promotion => (
                                                <Typography
                                                    {...styles.promotionNameText}
                                                    key={promotion.id}
                                                    data-test-selector="cartline_promotionName"
                                                >
                                                    {(cartLine.properties.isEOL && !cartLine.properties.choiceOfFreeProductPromotionId)
                                                        ? "" 
                                                        : promotion.name}
                                                </Typography>
                                            ))}
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            </Hidden>
                            <GridItem {...styles.productPartNumbersGridItem}>
                                {/* EC-490: Added isPromotionItem check to avoid inventory call for the promo product */}
                                {showInventoryAvailability && !cartLine.quoteRequired && !cartLine.isPromotionItem && (
                                    <ProductAvailability
                                        productId={cartLine.productId!}
                                        availability={cartLine.availability!}
                                        unitOfMeasure={cartLine.unitOfMeasure}
                                        trackInventory={cartLine.trackInventory}
                                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                        extendedStyles={styles.productAvailability}
                                    />
                                )}
                            </GridItem>
                            <GridItem {...styles.productPartNumbersGridItem}>
                                {showAddToList &&
                                    !hideAddToList &&
                                    cartLine.canAddToWishlist &&
                                    !cartLine.isRestricted && (
                                        <Link onClick={addToListClickHandler}>{translate("Add to List")}</Link>
                                    )}
                            </GridItem>
                            {cartLine.properties.isEligibleForFreeSelection?.toLowerCase() === "true" &&
                                !cartLine.properties.isEOL && (
                                    <GridItem {...styles.cartLineNotesGridItem}>
                                        <Button variant="primary" onClick={openPopup}>
                                            {translate(
                                                cartLine.properties.changePromotionalItem?.toLowerCase() === "true"
                                                    ? "Edit Free Items"
                                                    : "Select Free Items",
                                            )}
                                        </Button>
                                    </GridItem>
                                )}

                            {cartLine.properties.isEligibleForFreeSelection?.toLowerCase() === "true" &&
                                eolProducts &&
                                cartLine.productId &&
                                eolProducts[cartLine.productId] && (
                                    <GridItem {...styles.cartLineNotesGridItem}>
                                        <Button variant="primary" onClick={openEolPopup}>
                                            {translate(
                                                cartLine.properties.changePromotionalItem?.toLowerCase() === "true"
                                                    ? "Edit Free Items"
                                                    : "Select Free Items",
                                            )}
                                        </Button>
                                    </GridItem>
                                )}

                            {errorMessages.length > 0 && (
                                <GridItem {...styles.cartLineErrorMessageGridItem}>
                                    {errorMessages.map((message, index) => (
                                        <Typography
                                            {...styles.cartLineErrorMessageText}
                                            key={index}
                                            data-test-selector="cartline_errorMessage"
                                        >
                                            {message}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.productPriceAndAvailabilityGridItems}>
                        {!(userRoles && userRoles.includes(customBuyerRoleName)) && 
                            !cartLine.isPromotionItem &&
                            cartLine.properties?.promoFlag &&
                            cartLine.properties.promoFlag.toLowerCase() === "true" && (
                                <GridItem {...styles.limitedTimeOfferContainer}>
                                    <Typography as="span" {...styles.limitedTimeOfferLabelText}>
                                        {siteMessage("Limited_Time_Offer_Message")}
                                    </Typography>
                                </GridItem>
                            )}
                        
                            {!cartLine.quoteRequired &&
                                        !cart.cartNotPriced &&
                                        cartLine.pricing &&
                                        showPricing && (
                                    <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                        <Typography
                                            {...styles.extendedUnitNetPriceText}
                                            data-test-selector="cartline_extendedUnitNetPrice"
                                        >
                                            {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                : cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                        {enableVat && (
                                            <>
                                                <Typography as="p" {...styles.vatLabelText}>
                                                    {vatPriceDisplay === "DisplayWithVat" ||
                                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                        ? `${translate("Inc. VAT")} (${
                                                                cartLine.pricing.vatRate
                                                            }%)`
                                                        : translate("Ex. VAT")}
                                                </Typography>
                                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                    <>
                                                        <Typography {...styles.subtotalWithoutVatText}>
                                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                        </Typography>
                                                        <Typography as="p" {...styles.vatLabelText}>
                                                            {translate("Ex. VAT")}
                                                        </Typography>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </GridItem>
                             )}
                        {!cart.cartNotPriced && (
                            <ProductPrice
                                product={cartLine}
                                currencySymbol={cart.currencySymbol}
                                showSavings={!cart.properties["isPunchout"]}
                                showSavingsAmount={productSettings.showSavingsAmount}
                                showSavingsPercent={productSettings.showSavingsPercent}
                                showInvalidPriceMessage={true}
                                extendedStyles={styles.productPrice}
                                showLabel={false}
                                showUnitOfMeasure={showPerUnitPrice}
                            />
                        )}
                        <Hidden below="lg">
                            <GridItem {...styles.quantityGridItems}>
                                <CartLineQuantity
                                    cart={cart}
                                    editable={true}
                                    onQtyOrderedChange={qtyOrderedChangeHandler}
                                    extendedStyles={styles.quantity}
                                />
                            </GridItem>
                        </Hidden>
                        {/*  //Not Showing Promotion Name for EOL value parent item */}
                        {promotions?.map(promotion => (
                            <Typography
                                {...styles.promotionNameText}
                                key={promotion.id}
                                data-test-selector="orderLineCardExpandedPromotionName"
                            >
                                {(cartLine.properties.isEOL && !cartLine.properties.choiceOfFreeProductPromotionId)
                                    ? "" 
                                    : promotion.name}
                            </Typography>
                        ))}

                        {cartLine.isSubscription && cartLine.productSubscription && (
                            <ProductDeliveryScheduleButton
                                subscription={
                                    cartLine.properties.productSubscription
                                        ? JSON.parse(cartLine.properties.productSubscription)
                                        : cartLine.productSubscription
                                }
                                disabled={
                                    !cart.canModifyOrder ||
                                    cartLine.isPromotionItem ||
                                    !cartLine.qtyOrdered ||
                                    cartLine.qtyOrdered <= 0
                                }
                                onSave={saveScheduleHandler}
                                extendedStyles={styles.deliveryScheduleButton}
                            />
                        )}
                    </GridItem>
                    {cart.showCostCode && !cartLine.isPromotionItem && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            {cart.canEditCostCode ? (
                                <Select
                                    value={cartLine.costCode}
                                    onChange={costCodeChangeHandler}
                                    aria-label={translate("Cost Code")}
                                    {...styles.costCodeSelect}
                                >
                                    {cart.costCodes?.map(costCode => (
                                        <option key={costCode.costCode} value={costCode.costCode}>
                                            {costCode.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                            )}
                        </GridItem>
                    )}
                    {/* {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <GridItem {...styles.cartLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={true}
                                onNotesChange={notesChangeHandler}
                                extendedStyles={styles.cartLineNotes}
                            />
                        </GridItem>
                    )} */}
                    {isPopupOpen && (
                        <PromotionPopup
                            isPopupOpen={isPopupOpen}
                            freeQuantity={cartLine.properties.freeQuantity}
                            onHandleClose={closePopup}
                            cartLines={[...(cart.cartLines ?? [])]}
                            promotionType={currentPromotionType ?? undefined}
                            parentPromoProduct={cartLine}
                        />
                    )}
                </GridContainer>
            </GridItem>
            <GridItem {...styles.removeCartLineGridItem}>
                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));